import { useQuery, UseQueryOptions, UseQueryResult, QueryKey } from '@tanstack/react-query'
import { ApiResult } from '../types'
import { queryFnWithApiResult } from '../utils'

type UsePortalQueryOptions<TData, TSelect = TData | null, TQueryKey extends QueryKey = QueryKey> = Omit<
  UseQueryOptions<TData | null, Error, TSelect, TQueryKey>,
  'queryFn'
> & {
  queryFn: () => Promise<ApiResult<TData> | TData>
}

/**
 * A wrapper around react-query's useQuery that automatically handles ApiResult unwrapping.
 * The queryFn should return a Promise<ApiResult<T>> — no need to wrap with queryFnWithApiResult manually.
 *
 * Please pair with an exported queryKey factory for easy invalidation.
 */
const usePortalQuery = <TData, TSelect = TData | null, TQueryKey extends QueryKey = QueryKey>(
  options: UsePortalQueryOptions<TData, TSelect, TQueryKey>
): UseQueryResult<TSelect, Error> => {
  const { queryFn, ...rest } = options
  return useQuery<TData | null, Error, TSelect, TQueryKey>({
    ...rest,
    queryFn: queryFnWithApiResult(queryFn),
  })
}

export default usePortalQuery
